/**
 * "Who Owes Me Money?" — the 4th doorway question.
 *
 * Reads /api/debtors (cached AR list + aged buckets) and gives the owner a
 * one-tap "Send reminder" button per invoice. Firm-side staff can hit the
 * same page via a client_id query param (not exposed in the portal nav).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, MailPlus, Loader2, ExternalLink, Send, AlertTriangle, Users, WifiOff } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
const NZD2 = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 });

const BUCKET_ORDER = ["current", "d1_30", "d31_60", "d61_90", "d90_plus"];
const BUCKET_TONE = {
    current:  { bg: "bg-slate-50",   border: "border-slate-200",  text: "text-slate-900", accent: "text-slate-500" },
    d1_30:    { bg: "bg-amber-50",   border: "border-amber-200",  text: "text-amber-900", accent: "text-amber-600" },
    d31_60:   { bg: "bg-orange-50",  border: "border-orange-200", text: "text-orange-900", accent: "text-orange-600" },
    d61_90:   { bg: "bg-red-50",     border: "border-red-200",    text: "text-red-900",   accent: "text-red-600" },
    d90_plus: { bg: "bg-rose-100",   border: "border-rose-300",   text: "text-rose-950",  accent: "text-rose-700" },
};

export default function PortalDebtors() {
    const { activeClient, activeClientId } = useAuth();
    const isSoleTrader = activeClient?.entity_type === "sole_trader";
    const sourceLabel = isSoleTrader ? "Prosaic" : "Xero";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [reminder, setReminder] = useState(null); // { invoice, mailto, subject, body, to }

    const load = useCallback(async () => {
        const params = activeClientId ? { client_id: activeClientId } : {};
        try {
            const { data } = await api.get("/debtors", { params });
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [activeClientId]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    async function sync() {
        setSyncing(true);
        try {
            const params = activeClientId ? { client_id: activeClientId } : {};
            const { data } = await api.post("/debtors/sync", null, { params });
            setData(data);
            toast.success(`Xero synced — ${data.invoice_count} unpaid invoice${data.invoice_count === 1 ? "" : "s"} on the list.`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSyncing(false);
        }
    }

    async function openReminder(inv) {
        try {
            const params = activeClientId ? { client_id: activeClientId } : {};
            const { data: rem } = await api.post(`/debtors/${inv.invoice_id}/reminder`, null, { params });
            setReminder({ ...rem, invoice: inv });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const sortedInvoices = useMemo(() => {
        const rows = data?.invoices || [];
        return [...rows].sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0));
    }, [data]);

    if (loading) {
        return (
            <div className="flex items-center gap-3 text-[#5E636E] py-16 justify-center" data-testid="portal-debtors-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading debtors…
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-10" data-testid="portal-debtors">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl tracking-tight font-semibold">Who owes you money?</h1>
                    <p className="mt-1 text-sm text-[#5E636E]">
                        {activeClient?.name ? `${activeClient.name} • ` : ""}
                        Live from {sourceLabel} — chase overdue invoices with a single tap.
                    </p>
                </div>
                <Button
                    onClick={sync}
                    disabled={syncing || !data?.connected}
                    variant="outline"
                    className="shrink-0"
                    data-testid="debtors-sync-btn"
                >
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    {syncing ? "Syncing…" : `Sync ${sourceLabel}`}
                </Button>
            </div>

            {/* Not connected → CTA */}
            {!data?.connected && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 flex items-start gap-3" data-testid="debtors-not-connected">
                    <WifiOff className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium text-amber-950">Xero isn&apos;t linked yet</div>
                        <p className="text-sm text-amber-900/80 mt-0.5">
                            Ask your accountant to connect this entity to Xero so unpaid invoices show up here automatically.
                        </p>
                    </div>
                </div>
            )}

            {/* Hero total */}
            <div className="rounded-2xl border border-[#E4E4E7] bg-gradient-to-br from-slate-900 to-slate-700 text-white p-5 sm:p-6" data-testid="debtors-hero">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-white/70">Total owed to you</div>
                        <div className="mt-1 font-display text-4xl sm:text-5xl font-semibold" data-testid="debtors-total-owed">
                            {NZD.format(data?.total_owed || 0)}
                        </div>
                        <div className="mt-2 text-sm text-white/85">
                            Across {data?.invoice_count || 0} unpaid invoice{data?.invoice_count === 1 ? "" : "s"}
                            {data?.overdue_count > 0 && (
                                <>
                                    {" · "}
                                    <span className="text-rose-200 font-medium" data-testid="debtors-overdue-summary">
                                        {NZD.format(data.total_overdue)} overdue ({data.overdue_count})
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    {data?.last_synced_at && (
                        <div className="text-[11px] text-white/60">
                            Last synced {formatRelative(data.last_synced_at)}
                        </div>
                    )}
                </div>
            </div>

            {/* Aged buckets */}
            <section>
                <h2 className="font-display text-base font-semibold mb-2">Aged debtors</h2>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="debtors-buckets">
                    {BUCKET_ORDER.map((k) => {
                        const b = data?.buckets?.[k] || { label: "", count: 0, amount: 0 };
                        const t = BUCKET_TONE[k];
                        return (
                            <div
                                key={k}
                                className={`rounded-lg border ${t.border} ${t.bg} px-3 py-3`}
                                data-testid={`bucket-${k}`}
                            >
                                <div className={`text-[10px] font-medium uppercase tracking-wider ${t.accent}`}>{b.label}</div>
                                <div className={`mt-1 font-display text-xl font-semibold ${t.text}`}>{NZD.format(b.amount || 0)}</div>
                                <div className={`text-[11px] ${t.accent}`}>{b.count} invoice{b.count === 1 ? "" : "s"}</div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Invoice list */}
            <section>
                <h2 className="font-display text-base font-semibold mb-2">Unpaid invoices</h2>
                {sortedInvoices.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-[#E4E4E7] bg-white px-6 py-10 text-center" data-testid="debtors-empty">
                        <Users className="h-6 w-6 text-[#7A7F8B] mx-auto" />
                        <div className="mt-2 font-medium">You&apos;re all clear.</div>
                        <p className="text-sm text-[#5E636E] mt-1">
                            {data?.connected
                                ? `No unpaid invoices in ${sourceLabel} right now. Nice work.`
                                : `Once ${sourceLabel} is linked, unpaid invoices land here.`}
                        </p>
                    </div>
                ) : (
                    <div className="rounded-lg border border-[#E4E4E7] bg-white divide-y divide-[#F1F1F3]" data-testid="debtors-list">
                        {sortedInvoices.map((inv) => (
                            <InvoiceRow key={inv.invoice_id} inv={inv} onRemind={() => openReminder(inv)} />
                        ))}
                    </div>
                )}
            </section>

            {/* Reminder dialog */}
            <ReminderDialog
                reminder={reminder}
                sourceLabel={sourceLabel}
                onClose={() => setReminder(null)}
            />
        </div>
    );
}

function InvoiceRow({ inv, onRemind }) {
    const days = inv.days_overdue || 0;
    const badgeTone = days <= 0
        ? "bg-slate-100 text-slate-700"
        : days <= 30
            ? "bg-amber-100 text-amber-900"
            : days <= 60
                ? "bg-orange-100 text-orange-900"
                : "bg-rose-100 text-rose-900";
    const badgeText = days <= 0
        ? (inv.due_date ? `Due ${inv.due_date}` : "Not yet due")
        : `${days} day${days === 1 ? "" : "s"} overdue`;

    return (
        <div
            className="flex flex-wrap items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3"
            data-testid={`invoice-row-${inv.invoice_id}`}
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium truncate" data-testid={`invoice-contact-${inv.invoice_id}`}>
                        {inv.contact_name || "(no name)"}
                    </div>
                    {inv.invoice_number && (
                        <span className="text-xs text-[#5E636E]">#{inv.invoice_number}</span>
                    )}
                </div>
                <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                    <Badge className={`${badgeTone} border-0 rounded-full font-normal`}>
                        {days > 30 && <AlertTriangle className="h-3 w-3 mr-1" />}
                        {badgeText}
                    </Badge>
                    {inv.contact_email ? (
                        <span className="text-xs text-[#5E636E] truncate max-w-[220px]">{inv.contact_email}</span>
                    ) : (
                        <span className="text-xs text-amber-700">No email on file</span>
                    )}
                </div>
            </div>
            <div className="text-right">
                <div className="font-display font-semibold" data-testid={`invoice-amount-${inv.invoice_id}`}>
                    {NZD2.format(inv.amount_due || 0)}
                </div>
                <div className="text-[11px] text-[#5E636E]">
                    of {NZD2.format(inv.total || 0)}
                </div>
            </div>
            <Button
                size="sm"
                variant="outline"
                onClick={onRemind}
                className="shrink-0"
                data-testid={`invoice-remind-btn-${inv.invoice_id}`}
            >
                <MailPlus className="h-3.5 w-3.5" /> Send reminder
            </Button>
        </div>
    );
}

function ReminderDialog({ reminder, sourceLabel, onClose }) {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");

    useEffect(() => {
        if (reminder) {
            setSubject(reminder.subject || "");
            setBody(reminder.body || "");
        }
    }, [reminder]);

    if (!reminder) return null;
    const { to, invoice, has_contact_email: hasEmail } = reminder;
    const mailtoHref = hasEmail
        ? `mailto:${encodeURIComponent(to || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
        : null;

    return (
        <Dialog open={!!reminder} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl" data-testid="reminder-dialog">
                <DialogHeader>
                    <DialogTitle>Send a reminder</DialogTitle>
                    <DialogDescription>
                        {/* eslint-disable-next-line react/no-unescaped-entities */}
                        We've drafted a friendly chase for invoice{" "}
                        <span className="font-medium">{invoice?.invoice_number || invoice?.invoice_id}</span>.
                        Tweak the wording and send from your own inbox — no data leaves your device.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-wider text-[#5E636E]">To</label>
                        <div className="mt-1 rounded-md border border-[#E4E4E7] bg-[#F8F8F5] px-3 py-2 text-sm">
                            {hasEmail ? to : (
                                <span className="text-amber-700">
                                    No email on this contact in {sourceLabel} — copy the message below and send it however you normally chase them.
                                </span>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-[#5E636E]">Subject</label>
                        <input
                            className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-sm"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            data-testid="reminder-subject-input"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-[#5E636E]">Message</label>
                        <textarea
                            className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-sm font-mono leading-relaxed"
                            rows={8}
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            data-testid="reminder-body-input"
                        />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => {
                            navigator.clipboard.writeText(body).then(
                                () => toast.success("Message copied to clipboard"),
                                () => toast.error("Could not copy — please select and copy manually"),
                            );
                        }}
                        data-testid="reminder-copy-btn"
                    >
                        Copy message
                    </Button>
                    {hasEmail ? (
                        <a
                            href={mailtoHref}
                            className="inline-flex items-center gap-2 rounded-md bg-[#0E3B2E] hover:bg-[#0B2F25] text-white px-4 py-2 text-sm font-medium"
                            onClick={() => setTimeout(onClose, 300)}
                            data-testid="reminder-send-btn"
                        >
                            <Send className="h-4 w-4" /> Open in email app
                            <ExternalLink className="h-3 w-3 opacity-60" />
                        </a>
                    ) : (
                        <Button onClick={onClose} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white">
                            Done
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function formatRelative(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}
